import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Zap, LogOut, Mail, MessageCircle, BookOpen, Trash2,
  BarChart3
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "contacts", label: "Messages", icon: MessageCircle },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "stories", label: "Stories", icon: BookOpen },
];

const HTTP_UNAUTHORIZED = 401;

/* ─── Custom Hook: Admin Data Fetching ─── */

function useAdminData(user, axiosAuth) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState({ contacts: 0, subscribers: 0, stories: 0 });
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stories, setStories] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === "overview") {
        const [statsRes, contactsRes, subRes, storiesRes] = await Promise.all([
          axiosAuth.get("/admin/stats"),
          axiosAuth.get("/admin/contacts"),
          axiosAuth.get("/admin/subscribers"),
          axiosAuth.get("/admin/stories"),
        ]);
        setStats(statsRes.data);
        setContacts(contactsRes.data);
        setSubscribers(subRes.data);
        setStories(storiesRes.data);
      } else if (activeTab === "contacts") {
        const [statsRes, contactsRes] = await Promise.all([
          axiosAuth.get("/admin/stats"),
          axiosAuth.get("/admin/contacts"),
        ]);
        setStats(statsRes.data);
        setContacts(contactsRes.data);
      } else if (activeTab === "subscribers") {
        const res = await axiosAuth.get("/admin/subscribers");
        setSubscribers(res.data);
      } else if (activeTab === "stories") {
        const res = await axiosAuth.get("/admin/stories");
        setStories(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch admin data:", err.message);
      if (err.response?.status === HTTP_UNAUTHORIZED) {
        navigate("/admin/login");
      }
    }
  }, [activeTab, axiosAuth, navigate]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const handleDelete = useCallback(async (type, id) => {
    try {
      await axiosAuth.delete(`/admin/${type}/${id}`);
      fetchData();
    } catch (err) {
      console.error(`Failed to delete ${type}/${id}:`, err.message);
    }
  }, [axiosAuth, fetchData]);

  return { activeTab, setActiveTab, stats, contacts, subscribers, stories, handleDelete };
}

/* ─── Sub-components ─── */

const StatsGrid = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-white/10 mb-8">
    <div className="border border-white/10 p-6" data-testid="stat-contacts">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Messages</p>
      <p className="font-['Anton'] text-4xl text-[#FF3B30]">{stats.contacts}</p>
    </div>
    <div className="border border-white/10 p-6" data-testid="stat-subscribers">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Subscribers</p>
      <p className="font-['Anton'] text-4xl text-[#FFCC00]">{stats.subscribers}</p>
    </div>
    <div className="border border-white/10 p-6" data-testid="stat-stories">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Stories</p>
      <p className="font-['Anton'] text-4xl text-white">{stats.stories}</p>
    </div>
  </div>
);

const ContactRow = ({ contact, onDelete }) => (
  <div className="border border-white/10 p-6 group" data-testid={`contact-row-${contact.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 mb-2">
          <p className="font-semibold text-white">{contact.name}</p>
          <span className="text-xs text-white/30">{contact.email}</span>
        </div>
        <p className="text-sm text-white/60 leading-relaxed">{contact.message}</p>
        <p className="text-xs text-white/20 mt-3">{new Date(contact.created_at).toLocaleString()}</p>
      </div>
      <Button
        onClick={() => onDelete("contacts", contact.id)}
        variant="ghost"
        size="icon"
        className="rounded-none text-white/20 hover:text-[#FF3B30] hover:bg-red-500/10 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`delete-contact-${contact.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const SubscriberRow = ({ subscriber, onDelete }) => (
  <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-4 border-b border-white/10 items-center group" data-testid={`subscriber-row-${subscriber.id}`}>
    <span className="text-sm text-white truncate">{subscriber.email}</span>
    <span className="text-xs text-white/30">{new Date(subscriber.subscribed_at).toLocaleDateString()}</span>
    <Button
      onClick={() => onDelete("subscribers", subscriber.id)}
      variant="ghost"
      size="icon"
      className="rounded-none text-white/20 hover:text-[#FF3B30] hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
      data-testid={`delete-subscriber-${subscriber.id}`}
    >
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

const StoryRow = ({ story, onDelete }) => (
  <div className="border border-white/10 p-6 group" data-testid={`story-row-${story.id}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-white mb-1">{story.title}</h3>
        <p className="text-xs text-white/40 mb-3">
          By {story.author_name}{story.location ? ` from ${story.location}` : ""} — {new Date(story.created_at).toLocaleDateString()}
        </p>
        <p className="text-sm text-white/60 leading-relaxed line-clamp-3">{story.story}</p>
      </div>
      <Button
        onClick={() => onDelete("stories", story.id)}
        variant="ghost"
        size="icon"
        className="rounded-none text-white/20 hover:text-[#FF3B30] hover:bg-red-500/10 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`delete-story-${story.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

const OverviewTab = ({ stats, contacts }) => (
  <div data-testid="admin-overview">
    <h2 className="font-['Anton'] text-2xl sm:text-3xl uppercase tracking-tight mb-8">
      DASHBOARD <span className="text-[#FFCC00]">OVERVIEW</span>
    </h2>
    <StatsGrid stats={stats} />
    <h3 className="font-['Anton'] text-lg uppercase tracking-tight mb-4 text-white/80">Recent Messages</h3>
    <div className="space-y-0 border border-white/10">
      {contacts.slice(0, 5).map((c) => (
        <div key={c.id} className="border-b border-white/10 p-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{c.name} <span className="text-white/30 font-normal">({c.email})</span></p>
            <p className="text-xs text-white/50 mt-1 line-clamp-2">{c.message}</p>
          </div>
          <span className="text-xs text-white/30 shrink-0">{new Date(c.created_at).toLocaleDateString()}</span>
        </div>
      ))}
      {contacts.length === 0 && <p className="p-4 text-sm text-white/30">No messages yet</p>}
    </div>
  </div>
);

const ContactsTab = ({ contacts, onDelete }) => (
  <div data-testid="admin-contacts-tab">
    <div className="flex items-center justify-between mb-8">
      <h2 className="font-['Anton'] text-2xl sm:text-3xl uppercase tracking-tight">
        CONTACT <span className="text-[#FF3B30]">MESSAGES</span>
      </h2>
      <span className="text-xs text-white/40">{contacts.length} total</span>
    </div>
    <div className="space-y-0">
      {contacts.map((c) => <ContactRow key={c.id} contact={c} onDelete={onDelete} />)}
      {contacts.length === 0 && <p className="text-sm text-white/30 py-8 text-center">No messages yet</p>}
    </div>
  </div>
);

const SubscribersTab = ({ subscribers, onDelete }) => (
  <div data-testid="admin-subscribers-tab">
    <div className="flex items-center justify-between mb-8">
      <h2 className="font-['Anton'] text-2xl sm:text-3xl uppercase tracking-tight">
        NEWSLETTER <span className="text-[#FFCC00]">SUBSCRIBERS</span>
      </h2>
      <span className="text-xs text-white/40">{subscribers.length} total</span>
    </div>
    <div className="border border-white/10">
      <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-6 py-3 border-b border-white/10 text-xs uppercase tracking-[0.2em] text-white/40 font-bold">
        <span>Email</span>
        <span>Date</span>
        <span></span>
      </div>
      {subscribers.map((s) => <SubscriberRow key={s.id} subscriber={s} onDelete={onDelete} />)}
      {subscribers.length === 0 && <p className="text-sm text-white/30 py-8 text-center">No subscribers yet</p>}
    </div>
  </div>
);

const StoriesTab = ({ stories, onDelete }) => (
  <div data-testid="admin-stories-tab">
    <div className="flex items-center justify-between mb-8">
      <h2 className="font-['Anton'] text-2xl sm:text-3xl uppercase tracking-tight">
        COMMUNITY <span className="text-[#FF3B30]">STORIES</span>
      </h2>
      <span className="text-xs text-white/40">{stories.length} total</span>
    </div>
    <div className="space-y-0">
      {stories.map((s) => <StoryRow key={s.id} story={s} onDelete={onDelete} />)}
      {stories.length === 0 && <p className="text-sm text-white/30 py-8 text-center">No community stories yet</p>}
    </div>
  </div>
);

/* ─── Sidebar + Tabs ─── */

const Sidebar = ({ activeTab, setActiveTab }) => (
  <aside className="w-56 shrink-0 border-r border-white/10 min-h-[calc(100vh-64px)] hidden md:block" data-testid="admin-sidebar">
    <nav className="py-4">
      {TABS.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
              activeTab === tab.id
                ? "bg-white/5 text-[#FF3B30] border-l-2 border-[#FF3B30]"
                : "text-white/50 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
            }`}
            data-testid={`admin-tab-${tab.id}`}
          >
            <Icon className="w-4 h-4" />
            <span className="uppercase tracking-wider text-xs font-bold">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  </aside>
);

const MobileTabs = ({ activeTab, setActiveTab }) => (
  <div className="md:hidden w-full border-b border-white/10 flex overflow-x-auto">
    {TABS.map((tab) => {
      const Icon = tab.icon;
      return (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider font-bold whitespace-nowrap ${
            activeTab === tab.id
              ? "text-[#FF3B30] border-b-2 border-[#FF3B30]"
              : "text-white/50"
          }`}
        >
          <Icon className="w-3 h-3" /> {tab.label}
        </button>
      );
    })}
  </div>
);

/* ─── Main Dashboard ─── */

export default function AdminDashboard() {
  const { user, loading, logout, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const { activeTab, setActiveTab, stats, contacts, subscribers, stories, handleDelete } =
    useAdminData(user, axiosAuth);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/admin/login");
    }
  }, [user, loading, navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/admin/login");
  }, [logout, navigate]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#FF3B30]" />
            <span className="font-['Anton'] text-lg uppercase tracking-wider">
              EMF<span className="text-[#FF3B30]">AWARE</span>
            </span>
          </a>
          <span className="text-xs uppercase tracking-[0.2em] text-white/40 hidden sm:block">/ Admin Dashboard</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-white/50 hidden sm:block">{user.email}</span>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="rounded-none text-white/50 hover:text-[#FF3B30] hover:bg-white/5"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="flex">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <MobileTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        <main className="flex-1 p-6 md:p-8 max-w-full overflow-hidden">
          {activeTab === "overview" && <OverviewTab stats={stats} contacts={contacts} />}
          {activeTab === "contacts" && <ContactsTab contacts={contacts} onDelete={handleDelete} />}
          {activeTab === "subscribers" && <SubscribersTab subscribers={subscribers} onDelete={handleDelete} />}
          {activeTab === "stories" && <StoriesTab stories={stories} onDelete={handleDelete} />}
        </main>
      </div>
    </div>
  );
}
